import { Button } from "@faire/slate/components/button";
import { CheckOutlineSolid } from "@faire/slate-icons/CheckOutlineSolid";
import { motion, useReducedMotion } from "framer-motion";
import type { PropsWithChildren } from "react";

import {
  Confetti,
  EASE_OUT_EXPO,
  formatUsd,
  NET_TERMS,
  NET_TERMS_BENEFITS,
  useCountUp,
} from "./shared";
import { SignupStepHeader } from "./SignupStepHeader";

export interface NetTermsRevealScreenProps {
  confettiCount: number;
  onContinue: () => void;
}

/**
 * The payoff: an unambiguously celebratory reveal that frames net terms as a
 * gift Faire is handing the retailer. Big "Congratulations!", confetti, a
 * counting-up spending limit, and a benefit list that spells out why the terms
 * are good news — never a dry "your account has been approved" screen.
 *
 * Entrance uses per-element `FadeUp` with explicit delays rather than
 * variant-stagger propagation: variants inherited through AnimatePresence were
 * intermittently getting stuck at `opacity: 0`, so each element drives its own
 * animation independently instead.
 */
export const NetTermsRevealScreen: React.FC<NetTermsRevealScreenProps> = ({
  confettiCount,
  onContinue,
}) => {
  const animatedLimit = useCountUp(NET_TERMS.creditLimit, 1400, true);

  return (
    <motion.div
      exit={{ opacity: 0, transition: { duration: 0.4, ease: EASE_OUT_EXPO } }}
      className="bg-fs-surface-primary relative flex min-h-screen w-full flex-col overflow-hidden"
    >
      <SignupStepHeader progressPercent={100} />
      <Confetti pieceCount={confettiCount} />

      <div className="relative z-10 flex flex-1 flex-col items-center px-(--slate-spacing-layout-edge-mobile) py-(--slate-spacing-section-large)">
        <div className="flex w-full max-w-[520px] flex-col items-center gap-(--slate-spacing-group-xlarge) text-center">
          <ApprovedCheck />

          <div className="flex flex-col items-center gap-(--slate-spacing-group-medium)">
            <FadeUp delaySeconds={0.15}>
              <span className="type-fs-label-medium text-fs-text-success tracking-[0.14em] uppercase">
                {NET_TERMS.revealEyebrow}
              </span>
            </FadeUp>
            <FadeUp delaySeconds={0.25}>
              <h1 className="type-fs-display-l text-fs-text-primary text-balance">
                {NET_TERMS.revealHeadline}
              </h1>
            </FadeUp>
            <FadeUp delaySeconds={0.35}>
              <p className="type-fs-subheading text-fs-text-subdued text-balance">
                {NET_TERMS.revealSubhead}
              </p>
            </FadeUp>
          </div>

          <FadeUp delaySeconds={0.45} className="w-full">
            <SpendingLimitCard animatedLimit={animatedLimit} />
          </FadeUp>

          <FadeUp delaySeconds={0.55} className="w-full">
            <ul className="flex w-full flex-col gap-(--slate-spacing-group-medium) text-left">
              {NET_TERMS_BENEFITS.map((benefit) => (
                <li
                  key={benefit.title}
                  className="flex items-start gap-(--slate-dimensions-3)"
                >
                  <span className="bg-fs-surface-secondary mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full">
                    <benefit.Icon className="text-fs-icon-brand size-5" />
                  </span>
                  <div className="flex flex-col gap-(--slate-dimensions-1)">
                    <span className="type-fs-paragraph-medium text-fs-text-primary">
                      {benefit.title}
                    </span>
                    <span className="type-fs-label text-fs-text-subdued">
                      {benefit.detail}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </FadeUp>

          <FadeUp
            delaySeconds={0.65}
            className="flex w-full flex-col items-center gap-(--slate-spacing-group-medium)"
          >
            <p className="type-fs-label text-fs-text-subdued text-balance">
              {NET_TERMS.reassurance}
            </p>
            <div className="w-full max-w-[320px]">
              <Button variant="primary" onClick={onContinue} fullWidth>
                {NET_TERMS.ctaLabel}
              </Button>
            </div>
          </FadeUp>
        </div>
      </div>
    </motion.div>
  );
};

/** A single fade-and-rise entrance, collapsing to a no-op under reduced motion. */
const FadeUp: React.FC<
  PropsWithChildren<{ delaySeconds?: number; className?: string }>
> = ({ delaySeconds = 0, className, children }) => {
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: reduceMotion ? 0 : 0.55,
        delay: reduceMotion ? 0 : delaySeconds,
        ease: EASE_OUT_EXPO,
      }}
    >
      {children}
    </motion.div>
  );
};

const ApprovedCheck: React.FC = () => {
  const reduceMotion = useReducedMotion() ?? false;
  return (
    <motion.div
      initial={reduceMotion ? { opacity: 1, scale: 1 } : { scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 16 }}
      className="relative flex items-center justify-center"
    >
      <div
        aria-hidden
        className="bg-fs-message-surface-success absolute size-28 rounded-full opacity-50 blur-xl"
      />
      <div className="bg-fs-message-surface-success relative flex size-20 items-center justify-center rounded-full">
        <CheckOutlineSolid className="text-fs-icon-success size-10" />
      </div>
    </motion.div>
  );
};

const SpendingLimitCard: React.FC<{ animatedLimit: number }> = ({
  animatedLimit,
}) => {
  return (
    <div className="bg-fs-surface-secondary flex flex-col items-center gap-(--slate-dimensions-2) rounded-[20px] px-(--slate-spacing-element-large) py-(--slate-spacing-element-large)">
      <div className="flex items-center gap-(--slate-dimensions-2)">
        <span className="type-fs-label-medium text-fs-text-subdued">
          {NET_TERMS.limitLabel}
        </span>
        <span className="bg-fs-action-surface-default text-fs-text-primary-inverse type-fs-label rounded-full px-(--slate-dimensions-2) py-(--slate-dimensions-1)">
          {NET_TERMS.termBadgeLabel}
        </span>
      </div>
      <span className="type-fs-display-xl text-fs-text-primary tabular-nums">
        {formatUsd(animatedLimit)}
      </span>
      <span className="type-fs-label text-fs-text-subdued">
        to spend now, due in {NET_TERMS.termDays} days
      </span>
    </div>
  );
};
